import type { GameConfig } from "../../shared/types";

// Shared config-editing field builders, used by both the Lobby's inline
// gameplay-critical settings grid and the expert Settings modal. Each field
// takes a `getDraft` accessor rather than closing over a captured draft object
// directly, since the caller's `draft` binding can be *reassigned* (e.g.
// loading a preset) after a field has already been built - reading through an
// accessor at event time always sees the current one.

export interface FieldSpec {
  path: string[];
  label: string;
  step?: number;
  min?: number;
  max?: number;
  /** If set, the raw config value is multiplied by this for display and divided
   * by it when saving (e.g. scale:100 turns a 0-1 decimal into a 0-100 percent
   * field) - min/max are interpreted in the already-scaled (displayed) units. */
  scale?: number;
}

export function getDeep(obj: unknown, path: string[]): unknown {
  let current: unknown = obj;
  for (const key of path) {
    if (typeof current !== "object" || current === null) return undefined;
    current = (current as Record<string, unknown>)[key];
  }
  return current;
}

export function setDeep(obj: Record<string, unknown>, path: string[], value: unknown): void {
  let current: Record<string, unknown> = obj;
  for (let i = 0; i < path.length - 1; i++) {
    const key = path[i]!;
    if (typeof current[key] !== "object" || current[key] === null) current[key] = {};
    current = current[key] as Record<string, unknown>;
  }
  current[path[path.length - 1]!] = value;
}

export function buildNumberField(
  spec: FieldSpec,
  isHost: boolean,
  getDraft: () => GameConfig | null,
  onCommit?: () => void
): HTMLElement {
  const scale = spec.scale ?? 1;
  const row = document.createElement("label");
  row.className = "settings-field";
  const labelSpan = document.createElement("span");
  labelSpan.textContent = spec.label;
  const input = document.createElement("input");
  input.type = "number";
  input.step = String(spec.step ?? 1);
  if (spec.min !== undefined) input.min = String(spec.min);
  if (spec.max !== undefined) input.max = String(spec.max);
  input.value = String(Number(getDeep(getDraft(), spec.path) ?? 0) * scale);
  input.disabled = !isHost;
  input.addEventListener("change", () => {
    const draft = getDraft();
    if (!draft) return;
    const num = Number(input.value);
    let clamped = Number.isFinite(num) ? num : 0;
    if (spec.min !== undefined) clamped = Math.max(spec.min, clamped);
    if (spec.max !== undefined) clamped = Math.min(spec.max, clamped);
    input.value = String(clamped);
    setDeep(draft as unknown as Record<string, unknown>, spec.path, clamped / scale);
    onCommit?.();
  });
  row.append(labelSpan, input);
  return row;
}

export function buildSelectField(
  spec: { path: string[]; label: string },
  options: { value: string; label: string }[],
  isHost: boolean,
  getDraft: () => GameConfig | null,
  onCommit?: () => void
): HTMLElement {
  const row = document.createElement("label");
  row.className = "settings-field";
  const labelSpan = document.createElement("span");
  labelSpan.textContent = spec.label;
  const select = document.createElement("select");
  for (const opt of options) {
    const optionEl = document.createElement("option");
    optionEl.value = opt.value;
    optionEl.textContent = opt.label;
    select.appendChild(optionEl);
  }
  select.value = String(getDeep(getDraft(), spec.path) ?? "");
  select.disabled = !isHost;
  select.addEventListener("change", () => {
    const draft = getDraft();
    if (!draft) return;
    setDeep(draft as unknown as Record<string, unknown>, spec.path, select.value);
    onCommit?.();
  });
  row.append(labelSpan, select);
  return row;
}

/** A boolean field displayed as a two-option select ("Visible/Hidden",
 * "Enabled/Disabled", ...) rather than a checkbox, matching how this project's
 * lobby settings grid phrases every toggle as "Label: OptionA/OptionB". */
export function buildBooleanSelectField(
  spec: { path: string[]; label: string },
  trueLabel: string,
  falseLabel: string,
  isHost: boolean,
  getDraft: () => GameConfig | null,
  onCommit?: () => void
): HTMLElement {
  const row = document.createElement("label");
  row.className = "settings-field";
  const labelSpan = document.createElement("span");
  labelSpan.textContent = spec.label;
  const select = document.createElement("select");
  for (const [value, label] of [
    ["true", trueLabel],
    ["false", falseLabel],
  ] as const) {
    const optionEl = document.createElement("option");
    optionEl.value = value;
    optionEl.textContent = label;
    select.appendChild(optionEl);
  }
  select.value = getDeep(getDraft(), spec.path) ? "true" : "false";
  select.disabled = !isHost;
  select.addEventListener("change", () => {
    const draft = getDraft();
    if (!draft) return;
    setDeep(draft as unknown as Record<string, unknown>, spec.path, select.value === "true");
    onCommit?.();
  });
  row.append(labelSpan, select);
  return row;
}

export function buildCheckboxField(
  path: string[],
  labelText: string,
  isHost: boolean,
  getDraft: () => GameConfig | null
): HTMLElement {
  const row = document.createElement("label");
  row.className = "achievement-field";
  const checkbox = document.createElement("input");
  checkbox.type = "checkbox";
  checkbox.checked = !!getDeep(getDraft(), path);
  checkbox.disabled = !isHost;
  checkbox.addEventListener("change", () => {
    const draft = getDraft();
    if (!draft) return;
    setDeep(draft as unknown as Record<string, unknown>, path, checkbox.checked);
  });
  row.append(checkbox, document.createTextNode(" " + labelText));
  return row;
}

/** A small note/caption rendered inside a settings section box, for context
 * that applies to the whole group of fields (e.g. "(Values are per round.)"). */
export function buildFieldNote(text: string): HTMLElement {
  const note = document.createElement("p");
  note.className = "field-note";
  note.textContent = text;
  return note;
}
